// Which reminders to schedule, worked out on the device from what's stored on the device. The native
// app hands the result to iOS as local notifications, so nothing about plans, kitchens or health
// leaves the phone.
//
// Rules: only the kinds the person switched on; nothing in quiet hours; at most one per day (cooking
// timers aside); the next 7 days only; and no weight, goals, allergies or calories in the text, since
// it shows on the lock screen.

import { nextCheckInDate } from './checkin';
import { toUseFirst } from './freshness';
import { getRecipe } from './recipes';
import { displayName, type IngredientId } from './taxonomy';
import { addDays, parseISODate, todayISO } from '../lib/dates';
import type { PersistedState } from '../state/schema';

export type ReminderKind = 'dinner' | 'shop' | 'use_soon' | 'plan_next' | 'check_in';

export interface ReminderPrefs {
  dinner: boolean;
  /** "HH:MM", local time. */
  dinnerTime: string;
  /** Remind on days without a planned dinner too. */
  dinnerEveryDay: boolean;
  shop: boolean;
  useSoon: boolean;
  planNext: boolean;
  checkIn: boolean;
  timers: boolean;
  quietStart: string;
  quietEnd: string;
}

export interface Reminder {
  /** Stable per kind and day, so rescheduling replaces rather than duplicates. */
  id: number;
  kind: ReminderKind;
  at: Date;
  title: string;
  body: string;
  /** App route opened on tap. */
  route: string;
}

/** Priority when two reminders fall on the same day. */
const ORDER: ReminderKind[] = ['dinner', 'shop', 'use_soon', 'plan_next', 'check_in'];
const KIND_CODE: Record<ReminderKind, number> = { dinner: 1, shop: 2, use_soon: 3, plan_next: 4, check_in: 5 };
const DAYS = 7;

const minutes = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };
function at(dayISO: string, hhmm: string): Date {
  const d = parseISODate(dayISO);
  const [h, m] = hhmm.split(':').map(Number);
  d.setHours(h, m, 0, 0);
  return d;
}
/** True when the time of day falls in quiet hours (which may wrap past midnight). */
export function inQuietHours(d: Date, prefs: Pick<ReminderPrefs, 'quietStart' | 'quietEnd'>): boolean {
  const t = d.getHours() * 60 + d.getMinutes();
  const s = minutes(prefs.quietStart), e = minutes(prefs.quietEnd);
  return s <= e ? t >= s && t < e : t >= s || t < e;
}
const idFor = (kind: ReminderKind, day: string) => KIND_CODE[kind] * 100_000_000 + Number(day.replaceAll('-', ''));
const plural = (n: number, one: string) => `${n} ${n === 1 ? one : `${one}s`}`;

export function planReminders(s: PersistedState, prefs: ReminderPrefs, now = new Date()): Reminder[] {
  const today = todayISO(now);
  const days = Array.from({ length: DAYS }, (_, i) => addDays(today, i));
  const out: Reminder[] = [];
  const add = (kind: ReminderKind, day: string, time: string, title: string, body: string, route: string) =>
    out.push({ id: idFor(kind, day), kind, at: at(day, time), title, body, route });

  const planned = new Map((s.plan?.entries ?? []).filter((e) => e.status === 'planned').map((e) => [e.date, e]));

  if (prefs.dinner) {
    for (const day of days) {
      const entry = planned.get(day);
      const recipe = entry && getRecipe(entry.recipeId);
      if (recipe) add('dinner', day, prefs.dinnerTime, `Tonight: ${recipe.title}`, 'Tap for the recipe and anything you still need.', `/recipe/${recipe.id}`);
      else if (prefs.dinnerEveryDay && s.onboarded) add('dinner', day, prefs.dinnerTime, 'What’s for dinner?', 'Tonight’s pick is ready.', '/today');
    }
  }

  const openItems = s.list.filter((i) => !i.checked).length;
  if (prefs.shop && s.plan && openItems > 0) {
    const firstPlanned = [...planned.keys()].sort()[0];
    const day = firstPlanned ? addDays(firstPlanned, -1) : null;
    if (day && day >= today) add('shop', day, '18:30', `Shop for your week: ${plural(openItems, 'item')}`, 'Your list is sorted by aisle.', '/list');
  }

  // Marked "use soon", or near the end of its usual fridge life (an estimate).
  const soon = toUseFirst(s.kitchen.items, now.getTime()) as IngredientId[];
  if (prefs.useSoon && soon.length) {
    const names = soon.slice(0, 2).map(displayName);
    const more = soon.length > 2 ? ` and ${soon.length - 2} more` : '';
    add('use_soon', addDays(today, 1), '09:30', `Use soon: ${names.join(' and ')}${more}`, 'See dinners that use them.', '/today');
  }

  if (prefs.planNext && s.plan) {
    const end = addDays(s.plan.startDate, s.plan.entries.length);
    add('plan_next', end, '10:00', 'Plan next week?', 'Choose from ten dinners picked for you.', '/plan');
  }

  if (prefs.checkIn && s.onboarded) {
    const next = nextCheckInDate({ intervalMonths: s.profile.checkInMonths, startedAt: s.onboardedAt, history: s.checkIns, snoozedUntil: s.checkInSnoozedUntil });
    if (next) add('check_in', next < today ? addDays(today, 1) : next, '10:00', 'Time for a quick check-in', 'See how things are going.', '/you/check-in');
  }

  // In the future, outside quiet hours, within the window, and one per day by priority.
  const horizon = parseISODate(addDays(today, DAYS)).getTime();
  const byDay = new Map<string, Reminder>();
  for (const r of out.sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind))) {
    if (r.at.getTime() <= now.getTime() || r.at.getTime() >= horizon || inQuietHours(r.at, prefs)) continue;
    const day = todayISO(r.at);
    if (!byDay.has(day)) byDay.set(day, r);
  }
  return [...byDay.values()].sort((a, b) => a.at.getTime() - b.at.getTime());
}

/** Lock-screen text must never carry health data. Used by tests and as a last guard. */
export const SENSITIVE = /\b(kcal|calorie|weight|kg|lb|allerg|diet|goal|protein|carb)/i;
